import { backendApi } from "@/services/backend/api";
import { StructuralAssessmentCatalog } from "@/services/backend/htz/inspection/types/structural-assessment-catalog";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import { UUID } from "@bitory-gmbh/nidavellir";

const structuralAssessmentCatalogApi = backendApi
  .enhanceEndpoints({
    addTagTypes: [
      "HtzStructuralAssessmentCatalog",
      "HtzActiveStructuralAssessmentCatalog",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      htzStructuralAssessmentCatalogPut: build.mutation<
        StructuralAssessmentCatalog,
        PutRequest
      >({
        query: (request) => ({
          url: `/htz/inspection/structural-assessment-catalog`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzStructuralAssessmentCatalog", id: request.id },
          "HtzStructuralAssessmentCatalog",
        ],
      }),
      htzStructuralAssessmentCatalogShow: build.query<
        StructuralAssessmentCatalog,
        ShowRequest
      >({
        query: (request) => ({
          url: `/htz/inspection/structural-assessment-catalog`,
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("HtzStructuralAssessmentCatalog"),
      }),
      htzStructuralAssessmentCatalogList: build.query<
        Lst<StructuralAssessmentCatalog>,
        ListRequest
      >({
        query: (request) => ({
          url: `/htz/inspection/structural-assessment-catalog/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) =>
          providesList(result?.items, "HtzStructuralAssessmentCatalog"),
      }),
      htzStructuralAssessmentCatalogDelete: build.mutation<null, DeleteRequest>(
        {
          query: (request) => ({
            url: `/htz/inspection/structural-assessment-catalog`,
            method: "DELETE",
            body: request,
          }),
          invalidatesTags: ["HtzStructuralAssessmentCatalog"],
        },
      ),
      htzStructuralAssessmentCatalogPutActiveCatalog: build.mutation<
        ActiveCatalog,
        ActiveCatalog
      >({
        query: (request) => ({
          url: `htz/inspection/structural-assessment-catalog/active-catalog`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: ["HtzActiveStructuralAssessmentCatalog"],
      }),
      htzStructuralAssessmentCatalogShowActiveCatalog: build.query<
        ActiveCatalog,
        ShowActiveCatalogRequest
      >({
        query: (request) => ({
          url: `htz/inspection/structural-assessment-catalog/active-catalog`,
          method: "GET",
          params: request,
        }),
        providesTags: ["HtzActiveStructuralAssessmentCatalog"],
      }),
    }),
  });

export const {
  useHtzStructuralAssessmentCatalogPutMutation,
  useHtzStructuralAssessmentCatalogShowQuery,
  useLazyHtzStructuralAssessmentCatalogShowQuery,
  useHtzStructuralAssessmentCatalogListQuery,
  useHtzStructuralAssessmentCatalogDeleteMutation,
  useHtzStructuralAssessmentCatalogPutActiveCatalogMutation,
  useHtzStructuralAssessmentCatalogShowActiveCatalogQuery,
  useLazyHtzStructuralAssessmentCatalogShowActiveCatalogQuery,
} = structuralAssessmentCatalogApi;

export type PutRequest = StructuralAssessmentCatalog;

export interface ShowRequest {
  id: string;
}

export interface ListRequest {
  name?: Filter<string>;
  company?: Filter<UUID>;
  limit?: number;
  offset?: number;
}

export interface DeleteRequest {
  id: string;
}

export interface ActiveCatalog {
  companyId: string;
  catalogId: string | null;
}

export interface ShowActiveCatalogRequest {
  companyId: string;
}
